import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Progress,
  Statistic,
  Row,
  Col,
  Divider,
  Tabs,
} from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { Currency } from "../../types/currency.type";
import {
  SalesTariffRule,
  TariffRewardMode,
  TariffRuleType,
  User,
} from "../../types/user.type";
import { crmApi } from "../../api/crmApi";
import { salesApi, SalesTransaction } from "../../api/salesApi";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../contexts/auth.context";
import { hasAdminAccess } from "../../utils/roleUtils";

const { Title, Text } = Typography;

type SalesUser = User & { salesProfile?: User["salesProfile"] };

interface SalesUserFormValues {
  username?: string;
  name?: string;
  password?: string;
  salaryAmount?: number;
  salaryCurrency?: Currency;
  tariffRules?: SalesTariffRule[];
}

const ruleTypeOptions = [
  {
    value: TariffRuleType.PriceMargin,
    label: "Маржа от прайса",
  },
  {
    value: TariffRuleType.OrderVolume,
    label: "Оборот заказа",
  },
  {
    value: TariffRuleType.OrderTurnoverPercent,
    label: "Процент от оборота заказа",
  },
  {
    value: TariffRuleType.CommonOrderTurnoverPercent,
    label: "Общий % от оборота (чужие заказы)",
  },
  {
    value: TariffRuleType.CommonPriceMarginPercent,
    label: "Общий % от маржи прайса (чужие заказы)",
  },
];

const rewardModeOptions = [
  {
    value: TariffRewardMode.PercentOfPrice,
    label: "Процент от прайса",
  },
  {
    value: TariffRewardMode.FixedAmount,
    label: "Фиксированная сумма",
  },
];

const currencyOptions = Object.values(Currency).map((currency) => ({
  value: currency,
  label: currency,
}));

const defaultRule: Pick<
  SalesTariffRule,
  "type" | "rewardMode" | "thresholdValue" | "rewardValue"
> = {
  type: TariffRuleType.PriceMargin,
  rewardMode: TariffRewardMode.PercentOfPrice,
  thresholdValue: 0,
  rewardValue: 0,
};

const SalesUsersPage: React.FC = () => {
  const { authState } = useAuth();
  const isAdmin = hasAdminAccess(authState.user?.role);
  const [salesUsers, setSalesUsers] = useState<SalesUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<SalesUser | null>(null);
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
  const [viewingUser, setViewingUser] = useState<SalesUser | null>(null);
  const [viewingUserTransactions, setViewingUserTransactions] = useState<{
    actual: SalesTransaction[];
    expected: SalesTransaction[];
  } | null>(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionTab, setTransactionTab] = useState<string>("actual");
  const [passwordModalOpen, setPasswordModalOpen] = useState<boolean>(false);
  const [passwordLoading, setPasswordLoading] = useState<boolean>(false);
  const [userPassword, setUserPassword] = useState<{ password: string; username: string; name: string } | null>(null);
  const [createForm] = Form.useForm<SalesUserFormValues>();
  const [editForm] = Form.useForm<SalesUserFormValues>();

  const fetchSalesUsers = async () => {
    setLoading(true);
    try {
      const data = await crmApi.getSalesUsers();
      setSalesUsers(data);
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось загрузить список.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesUsers();
  }, []);

  useEffect(() => {
    if (selectedUser && editModalOpen) {
      editForm.setFieldsValue({
        salaryAmount: selectedUser.salesProfile?.salaryAmount,
        salaryCurrency: selectedUser.salesProfile?.salaryCurrency,
        tariffRules: selectedUser.salesProfile?.tariffRules?.map(
          ({ _id, ...rest }) => rest
        ) || [],
      });
    } else {
      editForm.resetFields();
    }
  }, [selectedUser, editModalOpen, editForm]);

  const handleCreateSalesUser = async (values: SalesUserFormValues) => {
    setCreateLoading(true);
    try {
      const payload = {
        username: values.username!,
        name: values.name!,
        password: values.password!,
        salaryAmount: values.salaryAmount,
        salaryCurrency: values.salaryCurrency,
        tariffRules: (values.tariffRules || []).map((rule) => ({
          ...rule,
          thresholdValue: Number(rule.thresholdValue),
          rewardValue: Number(rule.rewardValue),
        })),
      };
      await crmApi.createSalesUser(payload);
      message.success("Продажник создан.");
      setCreateModalOpen(false);
      createForm.resetFields();
      fetchSalesUsers();
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось создать продажника.");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateSalesProfile = async (values: SalesUserFormValues) => {
    if (!selectedUser) return;
    setUpdateLoading(true);
    try {
      const payload = {
        salaryAmount: values.salaryAmount,
        salaryCurrency: values.salaryCurrency,
        tariffRules: (values.tariffRules || []).map((rule) => ({
          ...rule,
          thresholdValue: Number(rule.thresholdValue),
          rewardValue: Number(rule.rewardValue),
        })),
      };
      await crmApi.updateSalesProfile(selectedUser._id, payload);
      message.success("Профиль продажника обновлён.");
      setEditModalOpen(false);
      setSelectedUser(null);
      fetchSalesUsers();
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось обновить профиль.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteSalesUser = async (user: SalesUser) => {
    setDeletingId(user._id);
    try {
      await crmApi.deleteSalesUser(user._id);
      message.success("Продажник удалён.");
      fetchSalesUsers();
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось удалить продажника.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewPassword = async (user: SalesUser) => {
    if (!isAdmin) {
      message.error("Только админ может просматривать пароли.");
      return;
    }
    setPasswordLoading(true);
    setPasswordModalOpen(true);
    try {
      const passwordData = await crmApi.getSalesUserPassword(user._id);
      setUserPassword(passwordData);
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось получить пароль.");
      setPasswordModalOpen(false);
    } finally {
      setPasswordLoading(false);
    }
  };

  const columns: ColumnsType<SalesUser> = useMemo(
    () => [
      {
        title: "Имя",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Логин",
        dataIndex: "username",
        key: "username",
      },
      {
        title: "Зарплата",
        dataIndex: ["salesProfile", "salaryAmount"],
        key: "salary",
        render: (_, record) => {
          const salary = record.salesProfile?.salaryAmount;
          const currency = record.salesProfile?.salaryCurrency;
          return salary
            ? `${salary.toLocaleString()} ${currency || ""}`
            : "—";
        },
      },
      {
        title: "Тарифы",
        key: "tariffs",
        render: (_, record) => {
          const rules = record.salesProfile?.tariffRules || [];
          if (!rules.length) {
            return <Text type="secondary">Не назначены</Text>;
          }
          return (
            <Space size={[8, 4]} wrap>
              {rules.map((rule) => (
                <Tag key={rule._id || `${rule.name}-${rule.thresholdValue}`}>
                  {rule.name} • {rule.rewardMode === TariffRewardMode.PercentOfPrice
                    ? `${rule.rewardValue}%`
                    : `${rule.rewardValue.toLocaleString()}`
                  }
                </Tag>
              ))}
            </Space>
          );
        },
      },
      {
        title: "Создан",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (value: string | undefined) =>
          value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "—",
      },
      {
        title: "Действия",
        key: "actions",
        width: isAdmin ? 360 : 280,
        render: (_, record) => (
          <Space>
            <Button
              icon={<EyeOutlined />}
              onClick={async () => {
                setViewingUser(record);
                setViewModalOpen(true);
                // Загружаем транзакции продажника
                setLoadingTransactions(true);
                try {
                  const transactions = await salesApi.getSalesUserTransactions(record._id, { limit: 50 });
                  setViewingUserTransactions(transactions);
                } catch (error: any) {
                  console.error("Ошибка при загрузке транзакций:", error);
                } finally {
                  setLoadingTransactions(false);
                }
              }}
            >
              Посмотреть
            </Button>
            {isAdmin && (
              <Button
                icon={<KeyOutlined />}
                onClick={() => handleViewPassword(record)}
              >
                Пароль
              </Button>
            )}
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedUser(record);
                setEditModalOpen(true);
              }}
            >
              Тарифы
            </Button>
            <Popconfirm
              title="Удалить продажника?"
              description="Это действие нельзя отменить."
              okText="Удалить"
              cancelText="Отмена"
              okButtonProps={{ danger: true, loading: deletingId === record._id }}
              icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
              onConfirm={() => handleDeleteSalesUser(record)}
            >
              <Button danger icon={<DeleteOutlined />} loading={deletingId === record._id}>
                Удалить
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    []
  );

const getRuleTypeLabel = (type?: TariffRuleType) => {
  switch (type) {
    case TariffRuleType.PriceMargin:
      return "Маржа от прайса";
    case TariffRuleType.OrderVolume:
      return "Оборот заказа";
    case TariffRuleType.OrderTurnoverPercent:
      return "Процент от оборота";
    case TariffRuleType.CommonOrderTurnoverPercent:
      return "Общий % от оборота (чужие заказы)";
    case TariffRuleType.CommonPriceMarginPercent:
      return "Общий % от маржи прайса (чужие заказы)";
    default:
      return type || "";
  }
};

const renderTariffRulesForm = () => (
    <Form.List name="tariffRules">
      {(fields, { add, remove }) => (
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          {fields.map((field, index) => (
            <Card
              key={field.key}
              type="inner"
              title={`Тариф #${index + 1}`}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(field.name)}
                />
              }
            >
              <Form.Item
                {...field}
                name={[field.name, "name"]}
                label="Название"
                rules={[{ required: true, message: "Введите название тарифа" }]}
              >
                <Input placeholder="Например, Маржа 30%" />
              </Form.Item>
              <Space style={{ width: "100%" }} size="large" wrap>
                <Form.Item
                  {...field}
                  name={[field.name, "type"]}
                  label="Тип"
                  rules={[{ required: true, message: "Выберите тип" }]}
                  style={{ flex: 1, minWidth: 180 }}
                >
                  <Select options={ruleTypeOptions} />
                </Form.Item>
                <Form.Item
                  shouldUpdate={(prev, curr) =>
                    prev.tariffRules?.[field.name]?.type !==
                    curr.tariffRules?.[field.name]?.type
                  }
                  noStyle
                >
                  {({ getFieldValue }) => {
                    const type = getFieldValue(["tariffRules", field.name, "type"]);
                    if (type === TariffRuleType.OrderVolume) {
                      return (
                        <Form.Item
                          {...field}
                          name={[field.name, "thresholdValue"]}
                          label="Порог"
                          rules={[{ required: true, message: "Введите порог" }]}
                          style={{ flex: 1, minWidth: 180 }}
                        >
                          <InputNumber
                            min={0}
                            style={{ width: "100%" }}
                            placeholder="Напр., 200000000"
                          />
                        </Form.Item>
                      );
                    }
                    return (
                      <Form.Item
                        {...field}
                        name={[field.name, "thresholdValue"]}
                        label="Порог"
                        style={{ flex: 1, minWidth: 180 }}
                      >
                        <InputNumber
                          min={0}
                          style={{ width: "100%" }}
                          placeholder="Не требуется"
                          disabled
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Space>
              <Space style={{ width: "100%" }} size="large" wrap>
                <Form.Item
                  shouldUpdate={(prev, curr) =>
                    prev.tariffRules?.[field.name]?.type !==
                    curr.tariffRules?.[field.name]?.type
                  }
                  noStyle
                >
                  {({ getFieldValue }) => {
                    const type = getFieldValue(["tariffRules", field.name, "type"]);
                    const onlyPercentModes = [
                      TariffRuleType.OrderTurnoverPercent,
                      TariffRuleType.CommonOrderTurnoverPercent,
                      TariffRuleType.CommonPriceMarginPercent,
                    ];
                    const options = onlyPercentModes.includes(type)
                      ? rewardModeOptions.filter(
                          (opt) => opt.value === TariffRewardMode.PercentOfPrice
                        )
                      : rewardModeOptions;
                    return (
                      <Form.Item
                        {...field}
                        name={[field.name, "rewardMode"]}
                        label="Начисление"
                        rules={[{ required: true, message: "Выберите начисление" }]}
                        style={{ flex: 1, minWidth: 180 }}
                      >
                        <Select
                          options={options}
                          disabled={onlyPercentModes.includes(type)}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
                <Form.Item
                  {...field}
                  name={[field.name, "rewardValue"]}
                  label="Вознаграждение"
                  rules={[{ required: true, message: "Введите сумму или %" }]}
                  style={{ flex: 1, minWidth: 180 }}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    placeholder="Напр., 30"
                  />
                </Form.Item>
              </Space>
              <Form.Item
                {...field}
                name={[field.name, "description"]}
                label="Описание"
              >
                <Input.TextArea
                  rows={2}
                  placeholder="Дополнительные условия"
                />
              </Form.Item>
            </Card>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => add(defaultRule)}
            block
          >
            Добавить тариф
          </Button>
        </Space>
      )}
    </Form.List>
  );

  return (
    <Card style={{ margin: 24 }}>
      <Space
        direction="vertical"
        size="large"
        style={{ width: "100%" }}
      >
        <Space
          align="center"
          style={{ width: "100%", justifyContent: "space-between" }}
        >
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              Продажники
            </Title>
            <Text type="secondary">
              Управляйте доступом, окладами и тарифами отдела продаж
            </Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            Добавить продажника
          </Button>
        </Space>

        <Table
          rowKey="_id"
          columns={columns}
          dataSource={salesUsers}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Space>

      <Modal
        title="Добавить продажника"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          createForm.resetFields();
        }}
        okText="Создать"
        onOk={() => createForm.submit()}
        confirmLoading={createLoading}
        width={720}
      >
        <Form
          layout="vertical"
          form={createForm}
          onFinish={handleCreateSalesUser}
        >
          <Form.Item
            name="name"
            label="Имя"
            rules={[{ required: true, message: "Введите имя" }]}
          >
            <Input placeholder="Имя продажника" />
          </Form.Item>
          <Form.Item
            name="username"
            label="Логин"
            rules={[{ required: true, message: "Введите логин" }]}
          >
            <Input placeholder="Уникальный логин" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Пароль"
            rules={[{ required: true, message: "Введите пароль" }]}
          >
            <Input.Password placeholder="Минимум 6 символов" />
          </Form.Item>
          <Space size="large" style={{ width: "100%" }} wrap>
            <Form.Item
              name="salaryAmount"
              label="Фиксированная зарплата"
              style={{ flex: 1, minWidth: 200 }}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="Напр., 1000"
              />
            </Form.Item>
            <Form.Item
              shouldUpdate={(prev, curr) =>
                prev.salaryAmount !== curr.salaryAmount
              }
              style={{ flex: 1, minWidth: 200 }}
            >
              {({ getFieldValue }) => {
                const salaryAmount = getFieldValue("salaryAmount");
                return (
                  <Form.Item
                    name="salaryCurrency"
                    label="Валюта"
                    rules={
                      salaryAmount
                        ? [{ required: true, message: "Выберите валюту" }]
                        : []
                    }
                  >
                    <Select
                      options={currencyOptions}
                      placeholder="Выберите валюту"
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Space>
          {renderTariffRulesForm()}
        </Form>
      </Modal>

      <Modal
        title={`Тарифы: ${selectedUser?.name || ""}`}
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          setSelectedUser(null);
          editForm.resetFields();
        }}
        okText="Сохранить"
        onOk={() => editForm.submit()}
        confirmLoading={updateLoading}
        width={720}
      >
        <Form
          layout="vertical"
          form={editForm}
          onFinish={handleUpdateSalesProfile}
        >
          <Space size="large" style={{ width: "100%" }} wrap>
            <Form.Item
              name="salaryAmount"
              label="Фиксированная зарплата"
              style={{ flex: 1, minWidth: 200 }}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="Напр., 1000"
              />
            </Form.Item>
            <Form.Item
              shouldUpdate={(prev, curr) =>
                prev.salaryAmount !== curr.salaryAmount
              }
              style={{ flex: 1, minWidth: 200 }}
            >
              {({ getFieldValue }) => {
                const salaryAmount = getFieldValue("salaryAmount");
                return (
                  <Form.Item
                    name="salaryCurrency"
                    label="Валюта"
                    rules={
                      salaryAmount
                        ? [{ required: true, message: "Выберите валюту" }]
                        : []
                    }
                  >
                    <Select
                      options={currencyOptions}
                      placeholder="Выберите валюту"
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Space>
          {renderTariffRulesForm()}
        </Form>
      </Modal>

      <Modal
        title={`Данные продажника: ${viewingUser?.name || ""}`}
        open={viewModalOpen}
        onCancel={() => {
          setViewModalOpen(false);
          setViewingUser(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setViewModalOpen(false);
            setViewingUser(null);
          }}>
            Закрыть
          </Button>,
        ]}
        width={900}
      >
        {viewingUser && (() => {
          const stats = viewingUser.salesProfile?.currentMonthStats;
          const balance = viewingUser.salesProfile?.balance || 0;
          const expectedBalance = viewingUser.salesProfile?.expectedBalance || 0;
          const actualVolume = stats?.actualVolume || 0;
          const expectedVolume = stats?.expectedVolume || 0;
          const actualCommission = stats?.actualCommission || 0;
          const expectedCommission = stats?.expectedCommission || 0;
          const actualBonuses = stats?.actualBonuses || 0;
          const expectedBonuses = stats?.expectedBonuses || 0;
          const totalEarned = stats?.totalEarned || 0;

          // Вычисляем проценты для прогресс-баров
          const volumeProgress = expectedVolume > 0 ? (actualVolume / expectedVolume) * 100 : 0;
          const commissionProgress = expectedCommission > 0 ? (actualCommission / expectedCommission) * 100 : 0;
          const balanceProgress = expectedBalance > 0 ? (balance / expectedBalance) * 100 : 0;

          // Находим следующий порог бонуса
          const volumeRules = viewingUser.salesProfile?.tariffRules?.filter(
            (r) => r.type === TariffRuleType.OrderVolume
          ) || [];
          const sortedRules = [...volumeRules].sort((a, b) => a.thresholdValue - b.thresholdValue);
          const nextRule = sortedRules.find((r) => actualVolume < r.thresholdValue);
          const bonusProgress = nextRule && nextRule.thresholdValue > 0 
            ? (actualVolume / nextRule.thresholdValue) * 100 
            : 100;

          return (
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Card>
                    <Statistic
                      title="Имя продажника"
                      value={viewingUser.name}
                      valueStyle={{ fontSize: 18 }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card>
                    <Statistic
                      title="Логин"
                      value={viewingUser.username}
                      valueStyle={{ fontSize: 18 }}
                    />
                  </Card>
                </Col>
              </Row>

              <Card title="💰 Балансы продажника">
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Card size="small" style={{ background: "#f6ffed", borderColor: "#52c41a" }}>
                      <Statistic
                        title="Фактический баланс"
                        value={balance}
                        precision={0}
                        suffix="UZS"
                        valueStyle={{ color: "#52c41a", fontSize: 24, fontWeight: "bold" }}
                      />
                      {expectedBalance > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <Progress
                            percent={Math.min(balanceProgress, 100)}
                            strokeColor="#52c41a"
                            format={() => `${balanceProgress.toFixed(1)}% от ожидаемого`}
                          />
                        </div>
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} md={12}>
                    <Card size="small" style={{ background: "#e6f7ff", borderColor: "#1890ff" }}>
                      <Statistic
                        title="Ожидаемый баланс"
                        value={expectedBalance}
                        precision={0}
                        suffix="UZS"
                        valueStyle={{ color: "#1890ff", fontSize: 20 }}
                      />
                    </Card>
                  </Col>
                </Row>
              </Card>

              {stats && (
                <Card title="📊 Статистика за текущий месяц">
                  <Row gutter={[16, 16]}>
                    <Col xs={24}>
                      <Card size="small" title="Оборот продаж" style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                          <Col xs={24} sm={12}>
                            <Statistic
                              title="Фактический оборот"
                              value={actualVolume}
                              precision={0}
                              suffix="UZS"
                              valueStyle={{ color: "#52c41a", fontSize: 20 }}
                            />
                          </Col>
                          <Col xs={24} sm={12}>
                            <Statistic
                              title="Ожидаемый оборот"
                              value={expectedVolume}
                              precision={0}
                              suffix="UZS"
                              valueStyle={{ fontSize: 18 }}
                            />
                          </Col>
                        </Row>
                        {expectedVolume > 0 && (
                          <div style={{ marginTop: 16 }}>
                            <Progress
                              percent={Math.min(volumeProgress, 100)}
                              strokeColor={volumeProgress >= 100 ? "#52c41a" : "#1890ff"}
                              status={volumeProgress >= 100 ? "success" : "active"}
                              format={(percent) => `Выполнено ${percent?.toFixed(1)}%`}
                            />
                          </div>
                        )}
                      </Card>
                    </Col>

                    <Col xs={24}>
                      <Card size="small" title="Комиссии" style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                          <Col xs={24} sm={12}>
                            <Statistic
                              title="Фактическая комиссия"
                              value={actualCommission}
                              precision={0}
                              suffix="UZS"
                              valueStyle={{ color: "#52c41a", fontSize: 20 }}
                            />
                          </Col>
                          <Col xs={24} sm={12}>
                            <Statistic
                              title="Ожидаемая комиссия"
                              value={expectedCommission}
                              precision={0}
                              suffix="UZS"
                              valueStyle={{ fontSize: 18 }}
                            />
                          </Col>
                        </Row>
                        {expectedCommission > 0 && (
                          <div style={{ marginTop: 16 }}>
                            <Progress
                              percent={Math.min(commissionProgress, 100)}
                              strokeColor={commissionProgress >= 100 ? "#52c41a" : "#1890ff"}
                              status={commissionProgress >= 100 ? "success" : "active"}
                              format={(percent) => `Получено ${percent?.toFixed(1)}%`}
                            />
                          </div>
                        )}
                      </Card>
                    </Col>

                    <Col xs={24}>
                      <Card size="small" title="Бонусы">
                        <Row gutter={16}>
                          <Col xs={24} sm={12}>
                            <Statistic
                              title="Фактические бонусы"
                              value={actualBonuses}
                              precision={0}
                              suffix="UZS"
                              valueStyle={{ color: "#52c41a", fontSize: 20 }}
                            />
                          </Col>
                          <Col xs={24} sm={12}>
                            <Statistic
                              title="Ожидаемые бонусы"
                              value={expectedBonuses}
                              precision={0}
                              suffix="UZS"
                              valueStyle={{ fontSize: 18 }}
                            />
                          </Col>
                        </Row>
                        {nextRule && (
                          <div style={{ marginTop: 16 }}>
                            <div style={{ marginBottom: 8, fontSize: 12, color: "#666" }}>
                              Следующий бонус: {nextRule.name} (порог: {nextRule.thresholdValue.toLocaleString()} UZS)
                            </div>
                            <Progress
                              percent={Math.min(bonusProgress, 100)}
                              strokeColor={bonusProgress >= 100 ? "#52c41a" : "#faad14"}
                              status={bonusProgress >= 100 ? "success" : "active"}
                              format={(percent) => {
                                const remaining = nextRule.thresholdValue - actualVolume;
                                return remaining > 0 
                                  ? `Осталось ${remaining.toLocaleString()} UZS`
                                  : "Порог достигнут!";
                              }}
                            />
                          </div>
                        )}
                      </Card>
                    </Col>

                    <Col xs={24}>
                      <Card 
                        size="small" 
                        style={{ 
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          border: "none"
                        }}
                      >
                        <Statistic
                          title={<span style={{ color: "white" }}>Всего заработано за месяц</span>}
                          value={totalEarned}
                          precision={0}
                          suffix={<span style={{ color: "white" }}>UZS</span>}
                          valueStyle={{ color: "white", fontSize: 28, fontWeight: "bold" }}
                        />
                      </Card>
                    </Col>
                  </Row>
                </Card>
              )}

              <Row gutter={[16, 16]}>
                {viewingUser.salesProfile?.salaryAmount && (
                  <Col xs={24} sm={12}>
                    <Card>
                      <Statistic
                        title="Фиксированная зарплата"
                        value={viewingUser.salesProfile.salaryAmount}
                        suffix={viewingUser.salesProfile.salaryCurrency || ""}
                        valueStyle={{ fontSize: 20 }}
                      />
                    </Card>
                  </Col>
                )}
                {viewingUser.createdAt && (
                  <Col xs={24} sm={12}>
                    <Card>
                      <Statistic
                        title="Дата создания"
                        value={dayjs(viewingUser.createdAt).format("DD.MM.YYYY")}
                        valueStyle={{ fontSize: 18 }}
                      />
                    </Card>
                  </Col>
                )}
              </Row>

              {viewingUser.salesProfile?.tariffRules && viewingUser.salesProfile.tariffRules.length > 0 && (
                <Card title="⚙️ Тарифы продажника">
                  <Space direction="vertical" style={{ width: "100%" }} size="middle">
                    {viewingUser.salesProfile.tariffRules.map((rule, index) => (
                      <Card 
                        key={rule._id || index} 
                        size="small"
                        style={{ borderLeft: `4px solid #1890ff` }}
                      >
                        <Space direction="vertical" style={{ width: "100%" }} size="small">
                          <div>
                            <Tag color="blue" style={{ fontSize: 14, padding: "4px 12px" }}>
                              {rule.name}
                            </Tag>
                            <Tag color="default" style={{ marginLeft: 8 }}>
                              {getRuleTypeLabel(rule.type)}
                            </Tag>
                          </div>
                          <div>
                            <Text strong>Вознаграждение: </Text>
                            <Text>
                              {rule.rewardMode === TariffRewardMode.PercentOfPrice
                                ? `${rule.rewardValue}%`
                                : `${rule.rewardValue.toLocaleString()} UZS`}
                            </Text>
                          </div>
                          {rule.thresholdValue > 0 && (
                            <div>
                              <Text strong>Порог: </Text>
                              <Text>{rule.thresholdValue.toLocaleString()} UZS</Text>
                            </div>
                          )}
                          {rule.description && (
                            <div>
                              <Text type="secondary">{rule.description}</Text>
                            </div>
                          )}
                        </Space>
                      </Card>
                    ))}
                  </Space>
                </Card>
              )}

              {/* Транзакции продажника */}
              <Card title="📋 Транзакции продажника" loading={loadingTransactions}>
                {viewingUserTransactions && (
                  <Tabs
                    activeKey={transactionTab}
                    onChange={setTransactionTab}
                    items={[
                      {
                        key: "actual",
                        label: (
                          <Space>
                            <CheckCircleOutlined style={{ color: "#52c41a" }} />
                            <Text strong>Реальный баланс</Text>
                            <Tag color="green">
                              {viewingUserTransactions.actual?.length || 0}
                            </Tag>
                          </Space>
                        ),
                        children: (
                          <Table
                            columns={[
                              {
                                title: "Дата",
                                dataIndex: "createdAt",
                                key: "createdAt",
                                render: (date: string) => dayjs(date).format("DD.MM.YYYY HH:mm"),
                                width: 150,
                              },
                              {
                                title: "Тип",
                                dataIndex: "type",
                                key: "type",
                                render: (type: string) => {
                                  const labels: Record<string, string> = {
                                    commission: "Комиссия",
                                    actualVolumeBonus: "Бонус за объем",
                                    withdrawal: "Вывод средств",
                                    salary: "Зарплата",
                                  };
                                  const colors: Record<string, string> = {
                                    commission: "blue",
                                    actualVolumeBonus: "green",
                                    withdrawal: "red",
                                    salary: "purple",
                                  };
                                  return (
                                    <Tag color={colors[type] || "default"}>
                                      {labels[type] || type}
                                    </Tag>
                                  );
                                },
                                width: 150,
                              },
                              {
                                title: "Описание",
                                dataIndex: "description",
                                key: "description",
                                ellipsis: true,
                              },
                              {
                                title: "Сумма",
                                key: "amount",
                                render: (record: SalesTransaction) => {
                                  const isNegative = record.type === "withdrawal";
                                  return (
                                    <Text
                                      strong
                                      style={{
                                        color: isNegative ? "#ff4d4f" : "#52c41a",
                                        fontSize: 16,
                                      }}
                                    >
                                      {isNegative ? "-" : "+"}
                                      {record.amount.UZS.toLocaleString("ru-RU")} UZS
                                    </Text>
                                  );
                                },
                                width: 150,
                                align: "right" as const,
                              },
                            ]}
                            dataSource={viewingUserTransactions.actual || []}
                            rowKey="_id"
                            pagination={{ pageSize: 10 }}
                            size="small"
                            locale={{ emptyText: "Нет транзакций" }}
                          />
                        ),
                      },
                      {
                        key: "expected",
                        label: (
                          <Space>
                            <ClockCircleOutlined style={{ color: "#1890ff" }} />
                            <Text strong>Ожидаемый баланс</Text>
                            <Tag color="blue">
                              {viewingUserTransactions.expected?.length || 0}
                            </Tag>
                          </Space>
                        ),
                        children: (
                          <Table
                            columns={[
                              {
                                title: "Дата",
                                dataIndex: "createdAt",
                                key: "createdAt",
                                render: (date: string) => dayjs(date).format("DD.MM.YYYY HH:mm"),
                                width: 150,
                              },
                              {
                                title: "Тип",
                                dataIndex: "type",
                                key: "type",
                                render: (type: string) => {
                                  const labels: Record<string, string> = {
                                    commission: "Комиссия",
                                    expectedVolumeBonus: "Ожидаемый бонус",
                                  };
                                  const colors: Record<string, string> = {
                                    commission: "blue",
                                    expectedVolumeBonus: "orange",
                                  };
                                  return (
                                    <Tag color={colors[type] || "default"}>
                                      {labels[type] || type}
                                    </Tag>
                                  );
                                },
                                width: 150,
                              },
                              {
                                title: "Описание",
                                dataIndex: "description",
                                key: "description",
                                ellipsis: true,
                              },
                              {
                                title: "Сумма",
                                key: "amount",
                                render: (record: SalesTransaction) => (
                                  <Text
                                    strong
                                    style={{
                                      color: "#1890ff",
                                      fontSize: 16,
                                    }}
                                  >
                                    +{record.amount.UZS.toLocaleString("ru-RU")} UZS
                                  </Text>
                                ),
                                width: 150,
                                align: "right" as const,
                              },
                            ]}
                            dataSource={viewingUserTransactions.expected || []}
                            rowKey="_id"
                            pagination={{ pageSize: 10 }}
                            size="small"
                            locale={{ emptyText: "Нет транзакций" }}
                          />
                        ),
                      },
                    ]}
                  />
                )}
                {!viewingUserTransactions && !loadingTransactions && (
                  <Text type="secondary">Транзакции не загружены</Text>
                )}
              </Card>
            </Space>
          );
        })()}
      </Modal>

      <Modal
        title={`Пароль пользователя: ${userPassword?.name || ""}`}
        open={passwordModalOpen}
        onCancel={() => {
          setPasswordModalOpen(false);
          setUserPassword(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setPasswordModalOpen(false);
            setUserPassword(null);
          }}>
            Закрыть
          </Button>,
        ]}
        width={500}
      >
        {passwordLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Typography.Text>Загрузка...</Typography.Text>
          </div>
        ) : userPassword ? (
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <div>
              <Typography.Text strong>Логин: </Typography.Text>
              <Typography.Text>{userPassword.username}</Typography.Text>
            </div>
            <div>
              <Typography.Text strong>Пароль: </Typography.Text>
              <Input.Password
                value={userPassword.password}
                readOnly
                style={{ marginTop: 8 }}
              />
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Только админ может просматривать пароли пользователей.
            </Typography.Text>
          </Space>
        ) : null}
      </Modal>
    </Card>
  );
};

export default SalesUsersPage;

